/**
 * 多重比較補正。
 *
 * 1 つの対照群に対して複数の比較群を検定すると、比較の数だけ偽陽性の
 * 機会が増える。比較するのは対照群と各比較群なので、3 群なら比較は 2 回。
 * 有意水準 5% なら、すべて差がなくてもどれかが有意になる確率は約 10%
 * (1 - 0.95^2) になる。
 */

export type CorrectionMethod = 'none' | 'bonferroni' | 'holm'

export const CORRECTION_LABELS: Record<CorrectionMethod, string> = {
  none: '補正なし',
  bonferroni: 'Bonferroni',
  holm: 'Holm',
}

/**
 * p 値を多重比較補正する。null（検定不能な比較）は位置を保ったまま null で返す。
 *
 * 補正後 p 値は「その値を有意水準と直接比べればよい」ように正規化されている。
 */
export function adjustPValues(
  pValues: readonly (number | null)[],
  method: CorrectionMethod,
): (number | null)[] {
  if (method === 'none') return [...pValues]

  const valid = pValues
    .map((p, index) => ({ p, index }))
    .filter((entry): entry is { p: number; index: number } => entry.p !== null)

  const k = valid.length
  const adjusted: (number | null)[] = pValues.map(() => null)
  if (k === 0) return adjusted

  if (method === 'bonferroni') {
    for (const { p, index } of valid) {
      adjusted[index] = Math.min(1, p * k)
    }
    return adjusted
  }

  // Holm 法: p 値を昇順に並べ、i 番目に (k - i) を掛けて単調性を強制する。
  // Bonferroni より検出力が高く、同じく FWER を alpha 以下に抑える。
  const sorted = [...valid].sort((a, b) => a.p - b.p)
  let running = 0
  sorted.forEach(({ p, index }, i) => {
    running = Math.max(running, Math.min(1, (k - i) * p))
    adjusted[index] = running
  })
  return adjusted
}

/**
 * 信頼区間を同時被覆にするための有意水準。
 *
 * Bonferroni は alpha / k でそのまま同時信頼区間になる。
 * Holm は検定手順であり対応する同時信頼区間が自然に定まらないため、
 * 区間は名目水準のままにして UI 側で注記する。
 */
export function intervalAlpha(
  alpha: number,
  comparisonCount: number,
  method: CorrectionMethod,
): number {
  if (method === 'bonferroni' && comparisonCount > 1) {
    return alpha / comparisonCount
  }
  return alpha
}
